import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from 'reactstrap';
import PrimaryInput from '../ui/PrimaryInput';
import FormButtons from '../common/Form/FormButtons';
import { ILeaveDetail, IUnpaidLeaveItem } from '@/pages/HRM/Leave/LeaveDetail';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { callApi, IApiErrorResponse } from '@/api/callApi';
import { IDeleteResponse } from '@/interfaces/common.model';
import { capitalizeFirstLetter, showToastNotification } from '@/utility/helper';
import moment from 'moment';

interface IMarkUnpaidLeaveModalProps {
  isOpen: boolean;
  toggle: () => void;
  leaveDetail: ILeaveDetail | null;
  editData?: IUnpaidLeaveItem | null;
  onSuccess: () => void;
}

interface IMarkUnpaidForm {
  startDate: string;
  endDate: string;
  reason: string;
}

const MarkUnpaidLeaveModal: React.FC<IMarkUnpaidLeaveModalProps> = ({
  isOpen,
  toggle,
  leaveDetail,
  editData,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<IMarkUnpaidForm>();

  const selectedStartDate = watch('startDate');

  useEffect(() => {
    if (isOpen && leaveDetail) {
      if (editData) {
        const defaultStart = editData?.startDate
          ? moment(editData.startDate).format('YYYY-MM-DD')
          : '';
        const defaultEnd = editData?.endDate
          ? moment(editData.endDate).format('YYYY-MM-DD')
          : '';

        setValue('startDate', defaultStart);
        setValue('endDate', defaultEnd);
        setValue('reason', editData?.reason || '');
      } else {
        const defaultStart = leaveDetail?.startDate
          ? moment(leaveDetail.startDate).format('YYYY-MM-DD')
          : '';
        const defaultEnd = leaveDetail?.endDate
          ? moment(leaveDetail.endDate).format('YYYY-MM-DD')
          : '';

        setValue('startDate', defaultStart);
        setValue('endDate', defaultEnd);
        setValue('reason', '');
      }
    } else {
      reset();
    }
  }, [isOpen, leaveDetail, editData, setValue, reset]);

  const onSubmit = async (data: IMarkUnpaidForm) => {
    if (!leaveDetail?.id) {
      showToastNotification('danger', 'Leave ID not found');
      return;
    }

    if (
      data.startDate &&
      data.endDate &&
      moment(data.endDate).isBefore(moment(data.startDate))
    ) {
      showToastNotification('danger', 'End date cannot be before start date');
      return;
    }

    setIsLoading(true);
    try {
      const isEdit = Boolean(editData?.id);
      const API = isEdit
        ? `${API_URL + API_ENDPOINT.updateUnpaidLeave}?unPaidLeaveId=${editData?.id}`
        : `${API_URL + API_ENDPOINT.addUnpaidLeave}?leaveId=${leaveDetail.id}`;

      const payload = {
        userId: leaveDetail?.user?.id,
        startDate: data.startDate,
        endDate: data.endDate,
        reason: data.reason,
      };

      const response: IDeleteResponse = await callApi(API, {
        method: isEdit ? 'PUT' : 'POST',
        data: payload,
      });

      if (response?.status) {
        showToastNotification(
          'success',
          response?.message ||
            (isEdit
              ? 'Unpaid leave updated successfully'
              : 'Marked as unpaid leave successfully'),
        );
        toggle();
        onSuccess();
      } else {
        showToastNotification(
          'danger',
          response?.message ||
            (isEdit
              ? 'Failed to update unpaid leave'
              : 'Failed to mark as unpaid leave'),
        );
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      className="modal-dialog modal-dialog-centered modal-lg"
      isOpen={isOpen}
      toggle={toggle}
    >
      <div className="modal-content">
        <div className="modal-header p-4 border-0">
          <h1 className="modal-title fs-3 rajdhani-semibold">
            {editData ? 'Update Unpaid Leave' : 'Mark As Unpaid Leave'}
          </h1>
          <button
            type="button"
            className="btn-close"
            onClick={toggle}
            aria-label="Close"
          />
        </div>
        <div className="modal-body p-4">
          {leaveDetail?.user && (
            <div className="alert alert-light border mb-4">
              <div className="row">
                <div className="col-12 col-md-6 mb-2 mb-md-0">
                  <span className="text-muted d-block fs-6">Employee Name</span>
                  <strong>
                    {capitalizeFirstLetter(leaveDetail.user.name || '--')}
                  </strong>
                </div>
                <div className="col-12 col-md-6">
                  <span className="text-muted d-block fs-6">Employee Code</span>
                  <strong>{leaveDetail.user.employeeCode || '--'}</strong>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-12 col-md-6 mb-3">
                <PrimaryInput
                  label="Start Date"
                  required={true}
                  type="date"
                  id="unpaidStartDate"
                  {...register('startDate', {
                    required: 'Start date is required',
                  })}
                  errors={errors?.startDate?.message}
                />
              </div>
              <div className="col-12 col-md-6 mb-3">
                <PrimaryInput
                  label="End Date"
                  required={true}
                  type="date"
                  id="unpaidEndDate"
                  min={selectedStartDate || undefined}
                  {...register('endDate', {
                    required: 'End date is required',
                    validate: (value) => {
                      if (
                        selectedStartDate &&
                        moment(value).isBefore(moment(selectedStartDate))
                      ) {
                        return 'End date cannot be earlier than start date';
                      }
                      return true;
                    },
                  })}
                  errors={errors?.endDate?.message}
                />
              </div>
              <div className="col-12 mb-3">
                <div className="form-group">
                  <label
                    htmlFor="unpaidReason"
                    className="form-label d-flex gap-1"
                  >
                    Reason <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${errors?.reason ? 'is-invalid' : ''}`}
                    id="unpaidReason"
                    rows={4}
                    placeholder="Enter reason for unpaid leave..."
                    {...register('reason', {
                      required: 'Reason is required',
                      minLength: {
                        value: 3,
                        message: 'Reason must be at least 3 characters',
                      },
                    })}
                  />
                  {errors?.reason && (
                    <div className="invalid-feedback d-block">
                      {errors.reason.message}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <FormButtons isLoading={isLoading} toggleModal={toggle}>
                {editData ? 'Update' : 'Mark as Unpaid'}
              </FormButtons>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default MarkUnpaidLeaveModal;
